/**
 * commercialAnalytics.js
 *
 * Project-level commercial risk metrics — functions here compare
 * contracts against EACH OTHER (as a pool), unlike contractFinancials.js
 * whose functions each operate on a single contract in isolation.
 *
 * Pure functions only. No Supabase, no React state. Caller supplies
 * already-fetched arrays.
 */

/**
 * Computes Subcontractor Concentration Risk: how much of the project's
 * total active contract value sits in its single largest contract,
 * across both subcontracts and equipment contracts combined.
 *
 * @param {Array<{id, contract_no, contractor_name, contract_value, status}>} subcontracts
 * @param {Array<{id, contract_no, contractor_name, contract_value, status}>} equipmentContracts
 * @returns {{
 *   concentrationPct: number,
 *   totalActiveValue: number,
 *   activeContractsCount: number,
 *   largestContract: {id, contract_no, contractor_name, contract_value, contract_type} | null
 * }}
 */
export function computeConcentrationRisk(subcontracts, equipmentContracts) {
  const activeContracts = [
    ...(subcontracts || [])
      .filter(c => c.status === 'approved')
      .map(c => ({ ...c, contract_type: 'subcontract' })),
    ...(equipmentContracts || [])
      .filter(c => c.status === 'approved')
      .map(c => ({ ...c, contract_type: 'equipment' })),
  ]

  const totalActiveValue = activeContracts.reduce(
    (s, c) => s + (parseFloat(c.contract_value) || 0), 0
  )

  if (activeContracts.length === 0) {
    return {
      concentrationPct: 0,
      totalActiveValue: 0,
      activeContractsCount: 0,
      largestContract: null,
    }
  }

  const largestContract = activeContracts.reduce((max, c) =>
    (parseFloat(c.contract_value) || 0) > (parseFloat(max.contract_value) || 0) ? c : max
  )

  const concentrationPct = totalActiveValue > 0
    ? (parseFloat(largestContract.contract_value) || 0) / totalActiveValue * 100
    : 0

  return {
    concentrationPct,
    totalActiveValue,
    activeContractsCount: activeContracts.length,
    largestContract,
  }
}
